import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { formatDate } from '../../lib/format';
import { useMe } from '../auth/useMe';
import { fetchSubscription } from './plans-api';

const BILLING_PATH = '/settings/billing';
/** The notice appears this long before the trial ends (S380-5, AC-10-070). */
const NOTICE_MS = 3 * 24 * 60 * 60 * 1000;
const STALE_MS = 5 * 60_000;

/**
 * The shell's trial notice (spec 10 section 6, S380-5, AC-10-070): for admins,
 * from three days before the trial ends and after it ended while no package
 * is booked. Silent while loading, on errors and in every other state.
 */
export function TrialBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const canManage = useMe().data?.active_tenant?.permissions.includes('settings:manage') ?? false;
  const subscription = useQuery({
    queryKey: ['billing', 'subscription', 'shell'],
    queryFn: fetchSubscription,
    enabled: canManage,
    retry: false,
    staleTime: STALE_MS,
  });
  const data = subscription.data;
  if (!canManage || !data) return null;
  const endsAt = data.trial_ends_at ? Date.parse(data.trial_ends_at) : Number.NaN;
  const ending = data.status === 'trial' && Number.isFinite(endsAt) && endsAt - Date.now() <= NOTICE_MS;
  if (!ending && data.status !== 'trial_ended') return null;
  return (
    <div className="ui-blocked-banner ui-blocked-banner--shell" role="status">
      <Clock className="ui-blocked-banner__icon" aria-hidden="true" size={20} />
      <p className="ui-blocked-banner__text">
        {ending && data.trial_ends_at ? t('billing.trial.ending', { date: formatDate(data.trial_ends_at) }) : t('billing.trial.ended')}
      </p>
      {location.pathname === BILLING_PATH ? null : (
        <div className="ui-blocked-banner__actions">
          <Link className="ui-btn ui-btn--secondary" to={BILLING_PATH}>
            {t('billing.trial.action')}
          </Link>
        </div>
      )}
    </div>
  );
}
